import SearchIcon from '@mui/icons-material/Search'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useMemo } from 'react'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { FALLBACK_LABEL_COLOR, useLabelColors } from './labelColors'

interface LabelLegendProps {
  /** Whether the "find more like this" action should be offered */
  canFindSimilar?: boolean
  /** Called to find cells similar to a label (embedding similarity) */
  onFindSimilar?: (label: string) => void
}

/**
 * Compact per-label summary: each session label with its color, a count of
 * cells carrying it, and (when embeddings are available) a "find more like
 * this" action. Doubles as the color legend, so cell tags are easy to read.
 */
export const LabelLegend = ({
  canFindSimilar,
  onFindSimilar,
}: LabelLegendProps): JSX.Element | null => {
  const {
    labeling: { sessionLabels, labels },
  } = useLabelingSlice()
  const colors = useLabelColors()

  const counts = useMemo(() => {
    const tally: Record<string, number> = {}
    Object.values(labels).forEach((label) => {
      tally[label] = (tally[label] ?? 0) + 1
    })
    return tally
  }, [labels])

  if (sessionLabels.length === 0) return null

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      gap={2}
      sx={{ px: 2, py: 0.5, borderBottom: '1px solid #ececf3' }}
    >
      {sessionLabels.map((label) => {
        const count = counts[label] ?? 0
        return (
          <Stack key={label} direction="row" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '3px',
                backgroundColor: colors[label] ?? FALLBACK_LABEL_COLOR,
              }}
            />
            <Typography variant="caption">{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {count.toLocaleString()}
            </Typography>
            {canFindSimilar && onFindSimilar && count > 0 ? (
              <Tooltip title={`Find more cells like ${label}`}>
                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onFindSimilar(label)}>
                  <SearchIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        )
      })}
    </Stack>
  )
}

export default LabelLegend
